import { type z } from 'zod'

import { ERROR_CODE, type ErrorCode, isErrorCode } from '@/shared/errors/codes'
import { AppError } from '@/shared/errors/envelope'

import { type AppSupabaseClient } from './client'

/**
 * Typed Supabase RPC call.
 *
 * The database functions raise stable error codes as the exception message
 * (e.g. `INVALID_INPUT`, `RESOURCE_NOT_AVAILABLE`), so the code is taken from
 * the PostgREST error message when it is a known code. Anything else becomes
 * `INTERNAL_ERROR` and its text is dropped: it may contain SQL detail.
 *
 * Results are validated with zod so a contract drift fails loudly here rather
 * than as `undefined` deep inside a screen.
 */

export interface RpcError {
  readonly code?: string
  readonly message?: string
}

/** PostgREST JWT failures (expired / invalid token). */
const SESSION_CODES: ReadonlySet<string> = new Set(['PGRST301', 'PGRST302', 'PGRST303'])

export function rpcErrorToAppError(error: RpcError): AppError {
  const message = error.message ?? ''
  let code: ErrorCode = ERROR_CODE.INTERNAL_ERROR
  if (isErrorCode(message)) code = message
  else if (error.code !== undefined && SESSION_CODES.has(error.code)) {
    code = ERROR_CODE.SESSION_EXPIRED
  }
  return new AppError(code)
}

export async function callRpc<T>(
  client: AppSupabaseClient,
  fn: string,
  args: Record<string, unknown>,
  schema: z.ZodType<T>,
): Promise<T> {
  const result = await client.rpc(fn, args)
  if (result.error !== null) throw rpcErrorToAppError(result.error)
  const data: unknown = result.data
  const parsed = schema.safeParse(data)
  if (!parsed.success) {
    throw new AppError(ERROR_CODE.INTERNAL_ERROR, { cause: parsed.error })
  }
  return parsed.data
}
